import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Trash2, Copy, Upload, Image as ImageIcon, Check, RefreshCw, Search } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return iso || "";
  }
};

const MediaPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [deletingName, setDeletingName] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await a.listUploads({ q });
      setItems(r.items || []);
    } catch (e) {
      toast({ title: "Laden fehlgeschlagen", description: String(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line

  const filtered = items.filter((it) =>
    !q || it.name.toLowerCase().includes(q.toLowerCase())
  );

  const onUpload = async (filesList) => {
    const files = Array.from(filesList || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await a.uploadImages(fd);
      toast({ title: `${r.urls?.length || 0} Bild${(r.urls?.length || 0) === 1 ? "" : "er"} hochgeladen` });
      await load();
    } catch (err) {
      toast({ title: "Upload-Fehler", description: err?.response?.data?.detail || String(err), variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const toggle = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const copySelected = async () => {
    const urls = filtered
      .filter((it) => selected.has(it.name))
      .map((it) => `${apiBase}${it.url}`)
      .join("\n");
    if (!urls) return;
    try {
      await navigator.clipboard.writeText(urls);
      toast({ title: `${selected.size} URL${selected.size === 1 ? "" : "s"} kopiert` });
    } catch (e) {
      toast({ title: "Kopieren fehlgeschlagen", variant: "destructive" });
    }
  };

  const copyOne = async (url) => {
    try {
      await navigator.clipboard.writeText(`${apiBase}${url}`);
      toast({ title: "URL kopiert" });
    } catch (e) {
      toast({ title: "Kopieren fehlgeschlagen", variant: "destructive" });
    }
  };

  const onDelete = async (name) => {
    if (!window.confirm(`Bild "${name}" wirklich loeschen?`)) return;
    setDeletingName(name);
    try {
      await a.deleteUpload(name);
      setItems((prev) => prev.filter((it) => it.name !== name));
      setSelected((prev) => { const n = new Set(prev); n.delete(name); return n; });
      toast({ title: "Geloescht" });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.used_in) {
        const titles = detail.used_in.map((u) => `• ${u.title}`).join("\n");
        const force = window.confirm(
          `Dieses Bild wird in ${detail.used_in.length} Artikel(n) verwendet:\n\n${titles}\n\nTrotzdem loeschen? (Bilder erscheinen als "Broken" in diesen Artikeln)`
        );
        if (force) {
          try {
            await a.deleteUpload(name, true);
            setItems((prev) => prev.filter((it) => it.name !== name));
            toast({ title: "Geloescht (trotz Verwendung)" });
            return;
          } catch (e2) {
            toast({ title: "Loeschen fehlgeschlagen", description: e2?.response?.data?.detail || String(e2), variant: "destructive" });
          }
        }
      } else {
        toast({ title: "Loeschen fehlgeschlagen", description: typeof detail === "string" ? detail : String(err), variant: "destructive" });
      }
    } finally {
      setDeletingName(null);
    }
  };

  const onDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} Bilder wirklich loeschen?`)) return;
    const names = Array.from(selected);
    setDeletingName("__bulk__");
    try {
      const results = await Promise.allSettled(names.map((n) => a.deleteUpload(n)));
      const failed = results.filter((r) => r.status === "rejected").length;
      setItems((prev) => prev.filter((it) => !selected.has(it.name)));
      clearSelection();
      toast({ title: `${names.length - failed} geloescht${failed ? `, ${failed} fehlgeschlagen` : ""}` });
    } finally {
      setDeletingName(null);
    }
  };

  const totalSize = items.reduce((s, it) => s + (it.size || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-media-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Medien-Bibliothek</h2>
          <p className="mt-1 text-xs text-zinc-400">
            {items.length} Bild{items.length === 1 ? "" : "er"} · {formatSize(totalSize)} insgesamt · Speicherort: <code>/api/uploads/</code>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="ghost" className="text-zinc-300 hover:text-white" disabled={loading} data-testid="media-refresh">
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
          </Button>
          <label className="btn-primary cursor-pointer rounded-md px-3 py-2 text-xs font-semibold inline-flex items-center gap-1" data-testid="media-upload-label">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Hochladen
            <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }} disabled={uploading} data-testid="media-upload-input" />
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-[#0f141b] p-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nach Dateinamen filtern …"
            className="pl-9 bg-[#10141b] border-white/10 text-zinc-200"
            data-testid="media-search"
          />
        </div>
        {selected.size > 0 ? (
          <>
            <span className="rounded-full bg-[#7c4dff]/20 px-3 py-1 text-xs text-[#b39bff]" data-testid="media-selected-count">
              {selected.size} ausgewaehlt
            </span>
            <Button onClick={copySelected} variant="ghost" className="text-zinc-300 hover:text-white" data-testid="media-copy-selected">
              <Copy className="mr-1 h-4 w-4" /> URLs kopieren
            </Button>
            <Button onClick={onDeleteSelected} variant="destructive" className="bg-rose-600/80 hover:bg-rose-600" disabled={deletingName === "__bulk__"} data-testid="media-delete-selected">
              {deletingName === "__bulk__" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />} Loeschen
            </Button>
            <Button onClick={clearSelection} variant="ghost" className="text-zinc-500 hover:text-white text-xs">
              abbrechen
            </Button>
          </>
        ) : (
          <span className="text-xs text-zinc-500">Klick auf Bilder zum Auswaehlen – dann Mehrfach-URL-Kopie oder Loeschen.</span>
        )}
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0f141b] py-16 text-center">
          <ImageIcon className="mx-auto h-10 w-10 text-zinc-700" />
          <p className="mt-2 text-sm text-zinc-500">
            {items.length === 0 ? "Noch keine Bilder hochgeladen." : "Keine Treffer fuer diesen Filter."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6" data-testid="media-grid">
          {filtered.map((it) => {
            const isSel = selected.has(it.name);
            return (
              <div
                key={it.name}
                className={`group relative overflow-hidden rounded-lg border transition-all ${
                  isSel ? "border-[#7c4dff] ring-2 ring-[#7c4dff]/50" : "border-white/10 hover:border-white/30"
                }`}
                data-testid={`media-item-${it.name}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(it.name)}
                  className="block w-full aspect-square bg-[#0a0d12]"
                  data-testid={`media-tile-${it.name}`}
                >
                  <img
                    src={`${apiBase}${it.url}`}
                    alt={it.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                  {isSel && (
                    <div className="absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#7c4dff] text-white shadow-lg">
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                </button>
                <div className="border-t border-white/10 bg-[#0f141b] p-2 text-[10px]">
                  <div className="truncate font-mono text-zinc-300" title={it.name}>{it.name}</div>
                  <div className="mt-0.5 flex items-center justify-between text-zinc-500">
                    <span>{formatSize(it.size)}</span>
                    <span title={formatDate(it.created_at)}>{formatDate(it.created_at).split(",")[0]}</span>
                  </div>
                  {it.used_count > 0 && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-300"
                      title={it.used_in?.map((u) => u.title).join("\n")}
                      data-testid={`media-usage-${it.name}`}
                    >
                      verwendet in {it.used_count}
                    </div>
                  )}
                </div>
                {/* Quick-Actions onHover */}
                <div className="pointer-events-none absolute right-1.5 top-1.5 flex flex-col gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); copyOne(it.url); }}
                    className="pointer-events-auto rounded-md bg-black/70 p-1.5 text-white hover:bg-[#7c4dff]"
                    title="URL kopieren"
                    data-testid={`media-copy-${it.name}`}
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(it.name); }}
                    className="pointer-events-auto rounded-md bg-black/70 p-1.5 text-white hover:bg-rose-600"
                    title="Loeschen"
                    disabled={deletingName === it.name}
                    data-testid={`media-delete-${it.name}`}
                  >
                    {deletingName === it.name ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MediaPanel;
